/**
 * Shared vocabulary for the porting audit trail.
 *
 * These live here rather than in the porting backend lib because each one is
 * mirrored by a GraphQL enum in apps/cpaas-api/src/tn-porting/schema.graphql, so
 * both the API layer and the backend need them. That is the same reason
 * PORT_STATUS / PORT_EVENT / PORT_SUBMISSION_MODES live in this lib.
 *
 * Keeping them in a side-effect-free lib also means a consumer can import them
 * without loading the porting barrel, which constructs vendor connectors at module
 * scope and therefore cannot be imported without credentials.
 *
 * The audit *row* shapes (TnPortOrderAuditInput and friends) deliberately stay in the
 * backend lib — they describe a database insert, not shared vocabulary.
 */

export const TnPortOrderAuditAction = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  CANCEL: 'CANCEL',
  ACTIVATE: 'ACTIVATE',
  SCHEDULE_ACTIVATION: 'SCHEDULE_ACTIVATION',
  CANCEL_ACTIVATION: 'CANCEL_ACTIVATION',
  CHANGE_DUE_DATE: 'CHANGE_DUE_DATE',
  UPDATE_ADDRESS: 'UPDATE_ADDRESS',
  ADD_NOTE: 'ADD_NOTE',
  DELETE_NOTE: 'DELETE_NOTE',
  PRE_PROVISION: 'PRE_PROVISION',
  REMOVE_PRE_PROVISION: 'REMOVE_PRE_PROVISION',
  SYSTEM_ACTIVATE: 'SYSTEM_ACTIVATE',
  SYSTEM_RECONCILE: 'SYSTEM_RECONCILE',
  SYSTEM_COMPLETE_EXTERNAL: 'SYSTEM_COMPLETE_EXTERNAL',
  SYSTEM_COMPLETE_INTERNAL: 'SYSTEM_COMPLETE_INTERNAL',
  SYSTEM_STATUS_CHANGE: 'SYSTEM_STATUS_CHANGE',
  MANUAL_SUBMISSION: 'MANUAL_SUBMISSION',     // System skipped TP API create (over cap)
  MANUAL_PON_BACKFILL: 'MANUAL_PON_BACKFILL', // Admin pasted PON onto a manual row
  ROC_RESUBMISSION: 'ROC_RESUBMISSION',
  MIGRATE_FROM_SIDEWINDER: 'MIGRATE_FROM_SIDEWINDER', // POR-33: reconstructed a legacy sidewinder port order in cpaas
  SYSTEM_PARTIAL_REJECTION: 'SYSTEM_PARTIAL_REJECTION', // Carrier rejected some TNs; moved them to a reject order
  REMOVE_TNS: 'REMOVE_TNS',                   // POR-202: user removed TNs (in place, or kept TNs moved to a HOLD order)
  SYSTEM_RELEASE_HOLD: 'SYSTEM_RELEASE_HOLD', // POR-202: submitted a HOLD order once the order it replaces was cancelled
  SPLIT_PORT_ORDER: 'SPLIT_PORT_ORDER',       // POR-206: user split the order into several from an edited per-TN file
  PORT_BILLING_FAILED: 'PORT_BILLING_FAILED', // POR-229: recording NRC billing rows for a port event failed; admin only, replayable
  SET_BILLING_FLAGS: 'SET_BILLING_FLAGS',     // POR-229: admin set is_expedited / cancel_not_billable; admin only
  INTERNAL_PORT_OUT_CASE: 'INTERNAL_PORT_OUT_CASE', // Raised (or failed to raise) the Salesforce Port Out case for an internal port
} as const;
export type TnPortOrderAuditActionType = typeof TnPortOrderAuditAction[keyof typeof TnPortOrderAuditAction];

export const TnPortOrderAuditActor = { USER: 'USER', SYSTEM: 'SYSTEM' } as const;
export type TnPortOrderAuditActorType = typeof TnPortOrderAuditActor[keyof typeof TnPortOrderAuditActor];

export const TnPortOrderAuditVisibility = { ALL: 'ALL', ADMIN: 'ADMIN' } as const;
export type TnPortOrderAuditVisibilityType = typeof TnPortOrderAuditVisibility[keyof typeof TnPortOrderAuditVisibility];
